import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Card, Logo } from '../design-system/components';
import { api } from '../lib/api';

/**
 * Landing page for the parental-consent confirmation link in the email-plus
 * notice email. Reads the one-time token from the query string and confirms it
 * server-side. Public — the token is the authorization.
 */
type State = 'working' | 'done' | 'error';

export function ConsentConfirm() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<State>('working');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // guard React StrictMode double-invoke (token is single-use)
    ran.current = true;
    if (!token) {
      setState('error');
      return;
    }
    api('/api/auth/consent/confirm', { method: 'POST', body: JSON.stringify({ token }) })
      .then(() => setState('done'))
      .catch(() => setState('error'));
  }, [token]);

  return (
    <div className="kz-center">
      <Card style={{ maxWidth: 460, width: '100%', textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-4)' }}>
          <Logo height={36} />
        </div>
        {state === 'working' && <p className="kz-muted">Confirming your consent…</p>}
        {state === 'done' && (
          <>
            <h2 style={{ marginBottom: 'var(--space-2)' }}>Thank you — you’re all set ✓</h2>
            <p className="kz-muted" style={{ marginBottom: 'var(--space-5)' }}>
              Your parental consent is confirmed. You can manage your family, kids, and chats any time from your
              grown-up dashboard.
            </p>
            <Link to="/app"><Button>Go to your dashboard</Button></Link>
          </>
        )}
        {state === 'error' && (
          <>
            <h2 style={{ marginBottom: 'var(--space-2)' }}>This link didn’t work</h2>
            <p className="kz-muted" style={{ marginBottom: 'var(--space-5)' }}>
              The confirmation link is invalid or has already been used. If you’ve already confirmed, you’re good
              to go. Otherwise, sign in and resend the confirmation from your account settings.
            </p>
            <Link to="/parent"><Button variant="secondary">Sign in</Button></Link>
          </>
        )}
      </Card>
    </div>
  );
}
